import type {
  PlaceOrderResponseDto,
  OrderDetailResponseDto,
  OrderDetailApiData,
  OrderHistoryRow,
  OrderHistoryResponseDto,
  OrderHistoryFiltersResponseDto,
  OrderHistoryFilters,
  PaginationDto,
  CalculateFeesResponseDto,
  OrderFeeLineWireDto,
  OrderDocumentWireDto,
} from './orderContracts'
import type {
  Order,
  PlacedOrder,
  CalculateFees,
  OrderDetail,
  OrderFeeLine,
  OrderDocument,
} from '../types/orderTypes'

const toNumber = (raw: string | number | null | undefined): number => {
  const n = Number(raw ?? 0)
  return Number.isFinite(n) ? n : 0
}

export const parseOrderFeeLine = (line: OrderFeeLineWireDto): OrderFeeLine => ({
  code: line.code,
  label: line.label,
  rate: line.rate?.trim() || null,
  amount: toNumber(line.amount),
  vatAmount: toNumber(line.vatAmount),
  isOnTotalOrderValue: Boolean(line.isOnTotalOrderValue),
})

const parseFeeLines = (lines: OrderFeeLineWireDto[] | null | undefined): OrderFeeLine[] =>
  (lines ?? []).map(parseOrderFeeLine)

export const parseCalculateFees = (resp: CalculateFeesResponseDto): CalculateFees => {
  const d = resp.data ?? {}
  const fees = parseFeeLines(d.fees)
  const grossAmount = toNumber(d.grossAmount)
  // Older deployments omit the total; derive it from the lines so the summary
  // never shows AED 0 fees against a non-zero payable.
  const totalFeeAmount =
    d.totalFeeAmount != null ? toNumber(d.totalFeeAmount) : fees.reduce((s, f) => s + f.amount, 0)
  return {
    offeringId: d.offeringId ?? '',
    offeringTitle: d.offeringTitle ?? '',
    tokenPrice: toNumber(d.tokenPrice),
    tokensAvailable: toNumber(d.tokensAvailable),
    tokenQuantity: toNumber(d.tokenQuantity),
    grossAmount,
    totalFeeAmount,
    netAmount: d.netAmount != null ? toNumber(d.netAmount) : grossAmount + totalFeeAmount,
    minTokens: toNumber(d.minTokens ?? 1),
    maxTokens: toNumber(d.maxTokens),
    fees,
  }
}

export const parsePlaceOrderResponse = (resp: PlaceOrderResponseDto): PlacedOrder => {
  const data = resp.data
  return {
    id: data?.id ?? '',
    orderReference: data?.orderReference ?? '',
    orderStatus: data?.orderStatus ?? '',
    propertyName: data?.propertyName ?? '',
    tokenQuantity: Number(data?.tokenQuantity ?? 0),
    tokenPrice: data?.tokenPrice ?? '0',
    grossAmount: data?.grossAmount ?? '0',
    totalFeeAmount: data?.totalFeeAmount ?? '0',
    fees: parseFeeLines(data?.fees),
    netAmount: data?.netAmount ?? '0',
    currencyCode: data?.currencyCode ?? 'AED',
    walletAmount: data?.walletAmount ?? '0',
    pgAmount: data?.pgAmount ?? '0',
    paymentMethod: data?.paymentMethod ?? '',
    paymentConfirmedAt: data?.paymentConfirmedAt ?? '',
    createdAt: data?.createdAt ?? '',
    statusTracker: data?.statusTracker ?? [],
  }
}

export const parseOrderDetailResponse = (resp: OrderDetailResponseDto): OrderDetail => {
  const data: Partial<OrderDetailApiData> = resp.data ?? {}
  return {
    id: data.id ?? '',
    orderReference: data.orderReference ?? '',
    orderStatus: data.orderStatus ?? '',
    displayStatus: data.displayStatus ?? data.orderStatus ?? '',
    offeringTitle: data.offeringTitle ?? '',
    propertyLocation: data.propertyLocation ?? '',
    propertyImage: data.propertyImage ?? null,
    statusTracker: Array.isArray(data.statusTracker) ? data.statusTracker : [],
    fundingProgress: {
      fundedPercentage: Number(data.fundingProgress?.fundedPercentage ?? 0),
      remainingAmount: data.fundingProgress?.remainingAmount ?? '0',
      currencyCode: data.fundingProgress?.currencyCode ?? 'AED',
      // null = no end date; keep it null so it never reads as "closes today".
      closingInDays:
        data.fundingProgress?.closingInDays != null
          ? Number(data.fundingProgress.closingInDays)
          : null,
      totalTokens: data.fundingProgress?.totalTokens ?? null,
      tokensSold: data.fundingProgress?.tokensSold ?? null,
      closedAt: data.fundingProgress?.closedAt ?? null,
    },
    listingId: data.listingId ?? null,
    createdAt: data.createdAt ?? '',
    updatedAt: data.updatedAt ?? '',
    modificationsUsed: Number(data.modificationsUsed ?? 0),
    modificationsAllowed: Number(data.modificationsAllowed ?? 0),
    tokenQuantity: Number(data.tokenQuantity ?? 0),
    tokenPrice: data.tokenPrice ?? '0.00',
    ownershipSharePct: data.ownershipSharePct ?? null,
    investmentValue: data.investmentValue ?? '0.00',
    totalApplicableFee: data.totalApplicableFee ?? '0',
    // Top-level per the swagger; the app's types nest them — accept either.
    fees: parseFeeLines(data.fees ?? data.feesBreakdown?.fees),
    totalPaid: data.totalPaid ?? '0.00',
    currencyCode: data.currencyCode ?? 'AED',
    paymentMethod: data.paymentMethod ?? '',
    transactionReference: data.transactionReference || null,
    canCancel: Boolean(data.canCancel ?? false),
    // Not live yet — null until the BE sends them.
    debitedAt: data.debitedAt ?? null,
    closedAt: data.closedAt ?? null,
    closedBy: data.closedBy ?? null,
    closeReason: data.closeReason?.trim() || null,
  }
}

const areaFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

// "apartment" / "store_unit" → "Apartment" / "Store Unit" — same casing the
// Properties cards use for the spec chip.
const formatPropertyType = (value: string | null | undefined): string | undefined =>
  value
    ?.split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ') || undefined

const toOptionalCount = (raw: number | null | undefined): number | undefined =>
  raw != null && Number.isFinite(Number(raw)) && Number(raw) > 0 ? Number(raw) : undefined

export const parseOrderHistoryRow = (row: OrderHistoryRow): Order => {
  const amount = toNumber(row.netAmount)
  const grossAmount =
    row.grossAmount != null
      ? toNumber(row.grossAmount)
      : row.tokenQuantity * toNumber(row.tokenPrice)
  return {
    id: row.id,
    offeringId: row.offeringId,
    propertyTitle: row.propertyName,
    imageUrl: row.propertyImage ?? undefined,
    trackingId: row.orderReference,
    date: row.createdAt,
    tokens: toNumber(row.tokenQuantity),
    pricePerToken: toNumber(row.tokenPrice),
    amount,
    grossAmount,
    totalFee:
      row.totalApplicableFee != null ? toNumber(row.totalApplicableFee) : amount - grossAmount,
    status: row.orderStatus,
    displayStatus: row.displayStatus ?? row.orderStatus,
    location: row.propertyLocation ?? undefined,
    ownershipSharePct: row.ownershipSharePct != null ? toNumber(row.ownershipSharePct) : null,
    totalTokens: row.totalTokens != null ? toNumber(row.totalTokens) : null,
    propertyType: formatPropertyType(row.propertyType),
    area:
      row.builtUpAreaSqm != null
        ? `${areaFormatter.format(toNumber(row.builtUpAreaSqm))} sqm`
        : undefined,
    bedrooms: toOptionalCount(row.numberOfBedrooms),
    bathrooms: toOptionalCount(row.numberOfBathrooms),
  }
}

export interface ParsedOrderHistory {
  rows: Order[]
  pagination: PaginationDto
}

export const parseOrderHistory = (resp: OrderHistoryResponseDto): ParsedOrderHistory => ({
  rows: (resp.data ?? []).map(parseOrderHistoryRow),
  pagination: resp.pagination ?? { page: 1, limit: 10, totalItems: 0, totalPages: 1 },
})

export const parseOrderHistoryFilters = (
  resp: OrderHistoryFiltersResponseDto,
): OrderHistoryFilters => ({
  status: resp.data?.status ?? [],
  dateRange: resp.data?.dateRange ?? [],
  investmentAmount: resp.data?.investmentAmount ?? [],
  sort: resp.data?.sort ?? [],
})

// ── Order documents ───────────────────────────────────────────────────────
// GET /orders/documents/{orderId}: the live API wraps rows in
// `{ success, message, data }`, swagger says a bare array — accept both.
// Anything else reads as "no documents yet", which is the normal state until
// the registry issues the certificate.

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const documentRows = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) return raw
  if (isRecord(raw) && Array.isArray(raw.data)) return raw.data
  return []
}

/** "352454" → 352454; a non-finite or 0 size is unknown. */
const toFileSize = (raw: OrderDocumentWireDto['fileSizeBytes']): number | null => {
  const bytes = Number(raw)
  return Number.isFinite(bytes) && bytes > 0 ? bytes : null
}

export const parseOrderDocument = (row: OrderDocumentWireDto): OrderDocument => ({
  id: row.documentId ?? '',
  type: row.documentType ?? '',
  title: row.documentTitle ?? '',
  fileName: row.fileName?.trim() ?? '',
  url: row.url?.trim() ?? '',
  sizeBytes: toFileSize(row.fileSizeBytes),
  mimeType: row.mimeType?.trim() || 'application/pdf',
  issuedAt: row.issuedAt ?? '',
})

export const parseOrderDocuments = (raw: unknown): OrderDocument[] =>
  documentRows(raw)
    .filter(isRecord)
    .map((row) => parseOrderDocument(row as OrderDocumentWireDto))
